import { useEffect, useState } from 'react';

import Icon from 'components/global/Icon';
import LottiePlayer from 'components/global/LottiePlayer';
import ProgressBar from 'components/global/ProgressBar';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import {
	faBarcode,
	faCalendarAlt,
	faCircle,
	faCircleCheck,
	faQrcode,
	faRoute,
	faTruck,
	faUser
} from '@fortawesome/pro-solid-svg-icons';

import InfoLabel from './component/InfoLabel';
import * as S from './styled/OrderTracking2.styled';

export const orderTracking2DefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'headerBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'headerTextColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'formTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'formTitleTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'iconColor'
	},

	{
		type: 'boolean',
		value: true,
		name: 'showCustomerName'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showDeliveryCompany'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderDate'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showTrackingNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showTrackingUrl'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showIcons'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author Burak Ak
 */

const OrderTracking2 = (props) => {
	const { customerName, deliveryCompany, orderDate, orderNumber, trackingNumber, trackingURL, status, propJson } =
		props;

	const { getLocaleDate } = useLocale();

	const [currentIndex, setCurrentIndex] = useState(0);
	const [trackingStatus, setTrackingStatus] = useState(status);

	const orderStatus = status;

	useEffect(() => {
		if (
			orderStatus === 'receivedOrder' ||
			orderStatus === 'returned' ||
			orderStatus === 'confirmedReturn' ||
			orderStatus === 'canceled'
		) {
			setCurrentIndex(0);
			setTrackingStatus('processing');
		} else if (
			orderStatus === 'arrivedDestinationTerminal' ||
			orderStatus === 'arrivedDestination' ||
			orderStatus === 'pickedUp' ||
			orderStatus === 'inTransit' ||
			orderStatus === 'departedTerminal' ||
			orderStatus === 'processing' ||
			orderStatus === 'arrivedOriginTerminal' ||
			orderStatus === 'arrivedTerminal'
		) {
			setCurrentIndex(1);
			setTrackingStatus('pickedUp');
		} else if (orderStatus === 'outForDelivery') {
			setCurrentIndex(2);
			setTrackingStatus('outForDelivery');
		} else if (orderStatus === 'delivered') {
			setCurrentIndex(3);
			setTrackingStatus('delivered');
		}
	}, [orderStatus, trackingStatus, currentIndex]);

	const trackingComponents = [
		{ lottie: 'order-tracking2-1', title: 'processing' },
		{ lottie: 'order-tracking2-2', title: 'pickedUp' },
		{ lottie: 'order-tracking2-3', title: 'outForDelivery' },
		{ lottie: 'order-tracking2-4', title: 'delivered' }
	];

	return (
		<div>
			<S.Card formBackgroundColor={propJson?.formBackgroundColor}>
				<S.ContentRow>
					{trackingComponents.map((component, index) => (
						<S.TrackingContainer key={index}>
							<S.LottieContainer
								className={`${currentIndex >= index ? 'active' : ''} lottie-${index + 1}`}>
								<LottiePlayer name={component.lottie} speed={0.7} width="auto" height="10dvh" />
							</S.LottieContainer>
							<S.IconColumn>
								<S.IconContainer>
									<Icon
										size={currentIndex >= index ? 'large' : 'small'}
										icon={currentIndex >= index ? faCircleCheck : faCircle}
										color={currentIndex >= index ? 'var(--red)' : 'var(--content-2)'}
									/>
								</S.IconContainer>

								<Typography
									noWrap
									component="span"
									fontWeight="500"
									color={currentIndex >= index ? 'var(--secondaryColor)' : 'var(--content-2)'}>
									{component.title}
								</Typography>
							</S.IconColumn>
						</S.TrackingContainer>
					))}
					<S.Progressbar>
						<ProgressBar value={currentIndex * 33} color="red"></ProgressBar>
					</S.Progressbar>
				</S.ContentRow>
			</S.Card>
			<S.Card formBackgroundColor={propJson?.formBackgroundColor}>
				<S.StatusContainer headerBackgroundColor={propJson?.headerBackgroundColor}>
					<Typography
						variant="h2"
						fontWeight="500"
						color={propJson?.headerTextColor ? propJson?.headerTextColor : 'white'}>
						shipmentDetails
					</Typography>
				</S.StatusContainer>

				<S.TrackingContent>
					<div className="row row-cols-1 row-cols-lg-2">
						{propJson?.showOrderNumber && (
							<InfoLabel
								label="orderNumber"
								value={orderNumber}
								icon={propJson?.showIcons ? faBarcode : null}
								valueFontWeight="700"
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}

						{propJson?.showCustomerName && (
							<InfoLabel
								label="customerName"
								value={customerName}
								icon={propJson?.showIcons ? faUser : null}
								valueFontWeight="700"
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showDeliveryCompany && (
							<InfoLabel
								label="deliveryCompany"
								value={deliveryCompany}
								valueFontWeight="700"
								icon={propJson?.showIcons ? faTruck : null}
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showOrderDate && (
							<InfoLabel
								label="orderDate"
								value={getLocaleDate(orderDate)}
								valueFontWeight="700"
								icon={propJson?.showIcons ? faCalendarAlt : null}
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showTrackingNumber && (
							<InfoLabel
								label="trackingNumber"
								anchor={trackingNumber}
								valueFontWeight="700"
								anchorLink={trackingURL}
								icon={propJson?.showIcons ? faQrcode : null}
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showTrackingUrl && (
							<InfoLabel
								label="trackingURL"
								icon={propJson?.showIcons ? faRoute : null}
								anchor="clickHereToTrackOrder"
								valueFontWeight="700"
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
								anchorLink={trackingURL}
							/>
						)}
					</div>
				</S.TrackingContent>
			</S.Card>
		</div>
	);
};

export default OrderTracking2;
